import {cloneDeep, get} from 'lodash';

/**
 * Cập nhật hoặc chèn giá trị lồng nhau dựa trên đường dẫn mở rộng
 * @param {Object|Array} obj - Đối tượng cần cập nhật
 * @param {String} path - Đường dẫn (vd: "a.b.[1].c.{value=1}.d" hoặc "**.{position=1}.contents")
 * @param {...*} values - Các giá trị cần chèn vào
 * @returns {Object|Array} - Đối tượng đã được cập nhật
 */
export default function updateNestedValue(obj, path, ...values) {
  if (path === '' || path === null) {
    return values.length === 1 ? values[0] : values;
  }

  // Xử lý wildcard path
  if (path.includes('**')) {
    return updateWithWildcard(obj, path, values);
  }

  // Phân tích cú pháp đường dẫn thành mảng keys
  const keys = parsePathToKeys(path);

  // Clone sâu đối tượng gốc
  const result = cloneDeep(obj);

  // Trường hợp đặc biệt: chèn vào mảng gốc
  if (keys.length === 1 && typeof keys[0] === 'number' && Array.isArray(result)) {
    result.splice(keys[0], 1, ...values);
    return result;
  }

  // Điều hướng đến container chứa vị trí cuối cùng
  let container = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    if (typeof key === 'object' && key.type === 'condition') {
      // Xử lý điều kiện tìm kiếm {key=value}
      if (!Array.isArray(container)) {
        throw new Error(`Container at path is not an array for condition ${key.key}=${key.value}`);
      }

      const foundIndex = container.findIndex(
        item => item && item[key.key] !== undefined && item[key.key] === key.value
      );

      if (foundIndex === -1) {
        // Không tìm thấy, tạo phần tử mới
        const newItem = {[key.key]: key.value};
        container.push(newItem);
        container = newItem;
      } else {
        container = container[foundIndex];
      }
    } else {
      // Xử lý key thông thường
      const nextKey = keys[i + 1];
      const isNextIndex = typeof nextKey === 'number';
      const isNextCondition = typeof nextKey === 'object' && nextKey.type === 'condition';

      // Tự động tạo cấu trúc phù hợp nếu chưa tồn tại
      if (container[key] === undefined) {
        if (isNextIndex || isNextCondition) {
          container[key] = [];
        } else {
          container[key] = {};
        }
      }

      container = container[key];
    }
  }

  const lastKey = keys[keys.length - 1];

  // Xử lý key cuối cùng
  if (typeof lastKey === 'object' && lastKey.type === 'condition') {
    // Xử lý điều kiện tìm kiếm cho key cuối cùng
    if (!Array.isArray(container)) {
      throw new Error(
        `Container at path is not an array for condition ${lastKey.key}=${lastKey.value}`
      );
    }

    const foundIndex = container.findIndex(
      item => item && item[lastKey.key] !== undefined && item[lastKey.key] === lastKey.value
    );

    if (foundIndex === -1) {
      // Không tìm thấy, tạo phần tử mới
      const newItem = values.length === 1 ? values[0] : values;
      if (typeof newItem === 'object' && newItem !== null) {
        newItem[lastKey.key] = lastKey.value;
      }
      container.push(newItem);
    } else {
      // Cập nhật phần tử đã tồn tại
      if (values.length === 1) {
        container[foundIndex] = values[0];
      } else {
        container.splice(foundIndex, 1, ...values);
      }
    }
  } else if (Array.isArray(container) && typeof lastKey === 'number') {
    // Chèn vào mảng tại vị trí chỉ định
    container.splice(lastKey, 1, ...values);
  } else {
    // Gán giá trị cho thuộc tính
    container[lastKey] = values.length === 1 ? values[0] : values;
  }

  return result;
}

/**
 * Xử lý cập nhật với wildcard path
 * @param {Object|Array} obj - Đối tượng cần cập nhật
 * @param {String} path - Đường dẫn có chứa **
 * @param {Array} values - Các giá trị cần chèn
 * @returns {Object|Array} - Đối tượng đã được cập nhật
 */
function updateWithWildcard(obj, path, values) {
  const result = cloneDeep(obj);
  const [prefix, suffix] = path.split('**');

  // Tìm tất cả các đường dẫn khớp với pattern
  const matchingPaths = findWildcardPaths(
    result,
    prefix.replace(/\.$/, ''),
    suffix.replace(/^\./, '')
  );

  // Cập nhật tất cả các đường dẫn tìm được
  let updatedResult = result;
  for (const fullPath of matchingPaths) {
    updatedResult = updateNestedValue(updatedResult, fullPath, ...values);
  }

  return updatedResult;
}

/**
 * Tìm tất cả đường dẫn khớp với wildcard pattern
 * @param {Object|Array} obj - Đối tượng để tìm kiếm
 * @param {String} prefix - Phần prefix trước **
 * @param {String} suffix - Phần suffix sau **
 * @returns {Array<String>} - Mảng các đường dẫn đầy đủ
 */
function findWildcardPaths(obj, prefix, suffix) {
  const paths = [];

  // Bắt đầu từ root hoặc prefix
  let startNodes = [];
  if (prefix === '') {
    startNodes = [{obj, path: ''}];
  } else {
    const prefixValue = getNestedValue(obj, prefix);
    if (prefixValue !== undefined) {
      startNodes = [{obj: prefixValue, path: prefix}];
    }
  }

  // Tìm kiếm sâu từ các start nodes
  for (const {obj: startObj, path: basePath} of startNodes) {
    const foundPaths = findDeepPaths(startObj, suffix, basePath);
    paths.push(...foundPaths);
  }

  return paths;
}

/**
 * Tìm kiếm sâu các đường dẫn khớp với suffix
 * @param {*} obj - Đối tượng hiện tại
 * @param {String} suffix - Pattern cần tìm
 * @param {String} currentPath - Đường dẫn hiện tại
 * @returns {Array<String>} - Mảng các đường dẫn tìm được
 */
function findDeepPaths(obj, suffix, currentPath = '') {
  const paths = [];

  // Kiểm tra xem từ vị trí hiện tại có thể đi theo suffix không
  const testPath = currentPath ? `${currentPath}.${suffix}` : suffix;
  if (getNestedValue(obj, suffix) !== undefined) {
    paths.push(testPath);
  }

  // Đệ quy tìm kiếm sâu hơn
  if (typeof obj === 'object' && obj !== null) {
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const newPath = currentPath ? `${currentPath}.[${index}]` : `[${index}]`;
        paths.push(...findDeepPaths(item, suffix, newPath));
      });
    } else {
      Object.keys(obj).forEach(key => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        paths.push(...findDeepPaths(obj[key], suffix, newPath));
      });
    }
  }

  return paths;
}

/**
 * Parse đường dẫn thành mảng keys
 * @param {String} path - Đường dẫn cần parse
 * @returns {Array} - Mảng các keys
 */
function parsePathToKeys(path) {
  const keys = [];
  const regex = /\[(\d+)\]|(\{([^=]+)=([^}]+)\})|([^\.\[\]{}]+)/g;
  let match;

  while ((match = regex.exec(path)) !== null) {
    if (match[1] !== undefined) {
      // Chỉ số mảng: [1]
      keys.push(parseInt(match[1], 10));
    } else if (match[2] !== undefined) {
      // Điều kiện tìm kiếm: {key=value}
      keys.push({
        type: 'condition',
        key: match[3],
        value: parseValue(match[4])
      });
    } else if (match[5] !== undefined) {
      if (match[5].endsWith('[]')) {
        keys.push(match[5].slice(0, -2));
        keys.push('PUSH_FLAG');
      } else {
        keys.push(match[5]);
      }
    }
  }

  return keys;
}

/**
 * Parse giá trị từ string, tự động detect kiểu dữ liệu
 * @param {string} value - Giá trị cần parse
 * @returns {*} - Giá trị đã được parse
 */
function parseValue(value) {
  // Số nguyên
  if (/^-?\d+$/.test(value)) {
    return parseInt(value, 10);
  }

  // Số thực
  if (/^-?\d+\.\d+$/.test(value)) {
    return parseFloat(value);
  }

  // Boolean
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Null
  if (value === 'null') return null;

  // Undefined
  if (value === 'undefined') return undefined;

  // String (bỏ dấu nháy nếu có)
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }

  // Mặc định là string
  return value;
}

/**
 * Truy cập giá trị lồng nhau từ object bằng path có thể bao gồm
 * - Cách viết chuẩn: "blocks[0].contents[1]"
 * - Cách viết theo `position`: "{position=1}.contents.{position=11}"
 * - Cách viết với wildcard: "**.{position=1}.contents"
 */
export function getNestedValue(data, path) {
  if (!path) return data;

  // Xử lý wildcard path
  if (path.includes('**')) {
    return getWithWildcard(data, path);
  }

  if (!path.includes('{position=') && !path.includes('{') && !path.includes('[')) {
    // Dùng lodash.get nếu không có cú pháp đặc biệt
    return get(data, path);
  }

  // Parse path thành các parts
  const parts = path.match(/\{[^}]+\}|\[\d+\]|[^.\[\]{}]+/g);
  if (!parts) return undefined;

  let current = data;

  for (const part of parts) {
    if (!current) return undefined;

    if (part.startsWith('{') && part.endsWith('}')) {
      // Xử lý điều kiện {key=value}
      const conditionMatch = part.match(/\{([^=]+)=([^}]+)\}/);
      if (conditionMatch) {
        const [, key, value] = conditionMatch;
        const parsedValue = parseValue(value);

        if (Array.isArray(current)) {
          current = current.find(item => item && item[key] === parsedValue);
        } else {
          return undefined;
        }
      }
    } else if (part.startsWith('[') && part.endsWith(']')) {
      // Xử lý index [0]
      const index = parseInt(part.slice(1, -1), 10);
      if (Array.isArray(current)) {
        current = current[index];
      } else {
        return undefined;
      }
    } else {
      // Xử lý key thông thường
      current = current[part];
    }
  }

  return current;
}

/**
 * Truy cập giá trị với wildcard path
 * @param {*} data - Dữ liệu gốc
 * @param {String} path - Đường dẫn có chứa **
 * @returns {Array} - Mảng các giá trị tìm được
 */
function getWithWildcard(data, path) {
  const [prefix, suffix] = path.split('**');
  const results = [];

  // Tìm tất cả các đường dẫn khớp với pattern
  const matchingPaths = findWildcardPaths(
    data,
    prefix.replace(/\.$/, ''),
    suffix.replace(/^\./, '')
  );

  // Lấy giá trị từ tất cả các đường dẫn tìm được
  for (const fullPath of matchingPaths) {
    const value = getNestedValue(data, fullPath);
    if (value !== undefined) {
      results.push(value);
    }
  }

  // Trả về mảng các kết quả hoặc giá trị đơn nếu chỉ có 1 kết quả
  return results.length === 1 ? results[0] : results;
}

export function withClonedState(setter, targetKey = null) {
  if (targetKey) {
    return updater =>
      setter(prev => {
        const cloned = cloneDeep(prev);
        cloned[targetKey] = updater(cloned[targetKey]);
        return cloned;
      });
  }

  return updater => setter(prev => updater(cloneDeep(prev)));
}
